import React, { useState } from "react";

import { find, isEmpty, times } from "lodash/fp";
import { Virtuoso } from "react-virtuoso";

import Skeleton from "@mui/material/Skeleton";

import DetailsPanel from "@app/analytics/Home/components/DetailsPanel";
import FeedUpsell from "@app/analytics/Home/components/FeedUpsell";
import useHomePageFeed from "@app/analytics/Home/hooks/useHomePageFeed";
import type { FeedItem } from "@app/analytics/Home/types";
import ROUTES from "@app/common/routes";

import Stack, { Row } from "@rui/Stack";

import GreetingBanner from "./GreetingBanner";
import NoNotifications from "./NoNotifications";
import HomeFeedItem from "../components/HomeFeedItem";

const Loading = () => {
  return (
    <Stack variant="center" spacing={2} paddingY={2}>
      {times(
        (i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{
              margin: "auto",
              width: "490px",
              maxWidth: "100%",
              borderRadius: "4px",
              height: "200px",
            }}
          />
        ),
        10
      )}
    </Stack>
  );
};

const Newsfeed = () => {
  const { feed, isLoading, isNewUser } = useHomePageFeed();
  const [selectedFeedItemId, setSelectedFeedItemId] = useState<string | null>(
    null
  );

  if (isLoading) {
    return <Loading />;
  }

  const selectedFeedItem = find(
    { entityId: selectedFeedItemId },
    feed
  ) as FeedItem;

  const handleSelectFeedItem = (id: string) => {
    // TODO (ENG-9588): add id as query param
    setSelectedFeedItemId(id);
  };

  const handleDeselectFeedItem = () => setSelectedFeedItemId(null);

  // TODO (ENG-9588): actually determine how many "new" updates there are
  const numNewUpdates = feed.length;
  const hasFeedItems = feed.length > 0;

  return (
    <Stack flex={1}>
      <Row paddingY={2} spacing={3} justifyContent="center">
        <Stack variant="center" width="550px" maxWidth="550px" flex={1}>
          <GreetingBanner isNewUser={isNewUser} numNewUpdates={numNewUpdates} />
          {isEmpty(feed) ? (
            <NoNotifications />
          ) : (
            <>
              <Virtuoso
                useWindowScroll
                style={{ width: "100%" }}
                data={feed}
                itemContent={(i, content) => {
                  if (!content) return null;
                  return (
                    <HomeFeedItem
                      key={content.entityId}
                      {...content}
                      onClick={handleSelectFeedItem}
                    />
                  );
                }}
              />
              {isNewUser && (
                <FeedUpsell
                  title="Review your settings"
                  description="Welcome to your Read account! Review and customize your settings."
                  cta={{
                    label: "Review settings",
                    href: ROUTES.USER_SETTINGS_DEFAULT_SECTION,
                  }}
                />
              )}
            </>
          )}
        </Stack>
        {hasFeedItems && (
          <Stack sx={{ width: "300px" }}>
            <Stack
              spacing={2}
              sx={{
                position: "fixed",
                height: "calc(100vh - 108px)", // 60px for the ActionBar, 2*24px for y-axis padding
              }}
            >
              <DetailsPanel
                item={selectedFeedItem}
                onClose={handleDeselectFeedItem}
              />
            </Stack>
          </Stack>
        )}
      </Row>
    </Stack>
  );
};

export default Newsfeed;
